import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IoPower } from "react-icons/io5";
import Main from "../../components/layout/Main";
import DeviceCard, { CardSkeletons } from "../../components/device/DeviceCard";
import AppDeviceCard from "../../components/device/app/AppDeviceCard";
import { DEVICE_TYPES } from "../../constants/devices";
import usePropertyDevices from "../../hooks/usePropertyDevices";
import { Chips } from "./Floors";
import BulkPower from "./BulkPower";
import { CameraDevices } from "../Camera";
import { useAutomationFeature } from "../../automation/AutomationProvider";

const ACTIVE = "ACTIVE";
// With Automation on, the Camera tab is gone and cameras get the last chip
// (DevicesScreen.js, TopTabBar.js).
const CAMERAS = "CAMERAS";

// As in the app, the IR blaster tab lists each learned remote on its own card
// (app/components/TabBar/TopTabBar.js); a remote id appears once.
const remotesOf = (blasters) => {
  const seen = new Set();
  const cards = [];
  blasters.forEach((blaster) =>
    (blaster.remoteDevices ?? []).forEach((remote) => {
      if (seen.has(remote.remoteId)) return;
      seen.add(remote.remoteId);
      cards.push({ blaster, remote });
    })
  );
  return cards;
};

// Devices tab as in the app (app/screens/devices/DevicesScreen.js): running
// devices first, then one tab per device type in alphabetical order.
function Devices() {
  const location = useLocation();
  const navigate = useHistory();
  const { devices, error, reload } = usePropertyDevices();
  const { on: automationOn } = useAutomationFeature();
  const [tab, setTab] = useState(location.state?.tab ?? ACTIVE);

  const tabs = useMemo(() => {
    const types = [];
    (devices ?? []).forEach((device) => {
      if (device.deviceType && !types.includes(device.deviceType)) types.push(device.deviceType);
    });
    return [ACTIVE, ...types.sort(), ...(automationOn ? [CAMERAS] : [])];
  }, [devices, automationOn]);

  useEffect(() => {
    if (devices && !tabs.includes(tab)) setTab(ACTIVE);
  }, [devices, tabs, tab]);

  const shown = useMemo(() => {
    if (!devices || tab === CAMERAS) return null;
    if (tab === ACTIVE) return devices.filter((device) => device.props?.state === true);
    return devices.filter((device) => device.deviceType === tab);
  }, [devices, tab]);

  return (
    <Main header="tab" title="Devices">
      {error ? (
        <div className="hx-empty" role="alert">
          Couldn't load the devices. Check your connection and reload the page.
        </div>
      ) : (
        <>
          <Chips label="Device types" items={tabs.map((key) => ({ key, label: key }))} active={tab} onSelect={setTab} />
          {tab === CAMERAS ? (
            <CameraDevices />
          ) : (
            <div className="hx-grid" key={tab}>
              {shown === null ? (
                <CardSkeletons count={6} />
              ) : tab === DEVICE_TYPES.IRBM && remotesOf(shown).length ? (
                remotesOf(shown).map(({ blaster, remote }) => (
                  <AppDeviceCard
                    key={remote.remoteId}
                    name={remote.name}
                    floor={blaster.floor}
                    room={blaster.room}
                    type={remote.deviceType}
                    active={false}
                    centerIcon
                    ariaLabel={`${remote.name}, ${remote.brand ? `${remote.brand} ` : ""}remote. Open remote`}
                    onOpen={() => navigate.push("/remote", { data: blaster, remote })}
                  />
                ))
              ) : shown.length ? (
                shown.map((device) => <DeviceCard key={device.applianceId ?? device.deviceId} device={device} />)
              ) : (
                <div className="hx-empty-page" style={{ gridColumn: "1 / -1", minHeight: "40vh" }}>
                  <IoPower aria-hidden="true" />
                  <p>No {tab.toLowerCase().replace(/_/g, " ")} devices</p>
                </div>
              )}
            </div>
          )}
          {shown && tab !== DEVICE_TYPES.IRBM ? <BulkPower devices={shown} onDone={reload} /> : null}
        </>
      )}
    </Main>
  );
}

export default Devices;
